// =============================================================================
// trackerThunks.js
//
// Uso:
//   dispatch(saveEntry(formData, userId))
//   dispatch(removeEntry(id, userId, entryTitle))
//   dispatch(restoreEntry(id, userId, entryTitle))
//
// userId obtido via: const userId = useSelector(state => state.session.user.id)
// O UserName é resolvido internamente pelo backend via AspNetUsers.
// =============================================================================

import {
	fetchEntriesRequest, fetchEntriesSuccess, fetchEntriesFailure,
	fetchOneRequest,     fetchOneSuccess,     fetchOneFailure,
	createRequest,       createSuccess,       createFailure,
	updateRequest,       updateSuccess,       updateFailure,
	deleteRequest,       deleteSuccess,       deleteFailure,
} from '../actions/trackerActions';

import {
	apiGetAll,
	apiGetById,
	apiCreate,
	apiUpdate,
	apiDelete,
	apiRestore,
} from '../requests/trackerRequests';

// ── Mensagens de erro ──────────────────────────────────────────────────────
// O backend devolve { message } no corpo de qualquer resposta 403 (ver
// ActivityTrackerController.cs → Forbidden()). Se por algum motivo o corpo não vier
// (ex: erro de rede, versão antiga da API), cai para uma mensagem genérica
// de "sem permissão" quando o status é 401/403, em vez de mostrar o erro
// em bruto ao utilizador.
const errorMessage = (err, fallback) => {
	const status = err?.response?.status;
	if (err?.response?.data?.message) return err.response.data.message;
	if (status === 401 || status === 403) return 'You do not have permission to perform this action.';
	return fallback;
};

// ── Carregar todas as entradas ────────────────────────────────────────────────

export const loadEntries = () => (dispatch) => {
	dispatch(fetchEntriesRequest());

	apiGetAll()
		.then((res) => dispatch(fetchEntriesSuccess(res.data ?? [])))
		.catch((err) => dispatch(fetchEntriesFailure(
			err?.response?.data?.message ?? 'Failed to load activities.'
		)));
};

// ── Carregar uma entrada completa (para o modal de edição) ────────────────────

export const loadEntry = (id) => (dispatch) => {
	dispatch(fetchOneRequest());

	apiGetById(id)
		.then((res) => dispatch(fetchOneSuccess(res.data)))
		.catch((err) => dispatch(fetchOneFailure(
			err?.response?.data?.message ?? 'Failed to load activity.'
		)));
};

// ── Guardar (criar ou actualizar) ─────────────────────────────────────────────
//
// Uso: dispatch(saveEntry(formData, userId))

export const saveEntry = (formData, userId) => (dispatch) => {
	const isEditing = Boolean(formData.id);

	dispatch(isEditing ? updateRequest() : createRequest());

	const payload = {
		...formData,
		teamId:       formData.teamId  ? Number(formData.teamId)                   : null,
		ownerIds:     (formData.ownerIds ?? []).map(Number).filter((n) => !isNaN(n)),
		startDate:    formData.startDate || null,
		endDate:      formData.endDate   || null,
		auditUserId:  userId ?? 'unknown',
	};

	const request = isEditing
		? apiUpdate(payload.id, payload)
		: apiCreate(payload);

	request
		.then((res) => {
			if (isEditing) {
				dispatch(updateSuccess(res.data));
				dispatch(loadEntries());
			} else {
				dispatch(createSuccess(res.data));
				dispatch(loadEntries());
			}
		})
		.catch((err) => {
			const message = errorMessage(err, 'Failed to save activity.');
			dispatch(isEditing ? updateFailure(message) : createFailure(message));
		});
};

// ── Soft delete ───────────────────────────────────────────────────────────────
//
// Uso: dispatch(removeEntry(id, userId, entry.title))

export const removeEntry = (id, userId, entryTitle) => (dispatch) => {
	dispatch(deleteRequest());

	apiDelete(id, userId ?? 'unknown', entryTitle)
		.then(() => dispatch(deleteSuccess(id)))
		.catch((err) => dispatch(deleteFailure(
			errorMessage(err, 'Failed to delete activity.')
		)));
};

// ── Restore ───────────────────────────────────────────────────────────────────
//
// Uso: dispatch(restoreEntry(id, userId, entryTitle))

export const restoreEntry = (id, userId, entryTitle) => (dispatch) => {
	apiRestore(id, userId ?? 'unknown', entryTitle)
		.then(() => dispatch(loadEntries()))
		.catch((err) => console.error('Failed to restore entry:', errorMessage(err, 'Failed to restore activity.')));
};